import Box from '@mui/material/Box';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOpenXAgentContext } from '../../../context/useOpenXAgentContext.ts';
import { OpenXAgentFilters } from '../shared/OpenXAgentFilters.tsx';
import { OpenXAgentPanelHeader } from '../shared/OpenXAgentPanelHeader.tsx';
import { OpenXAgentPanelSection } from '../shared/OpenXAgentPanelSection.tsx';
import { OpenXAgentStatTiles } from '../shared/OpenXAgentStatTiles.tsx';
import { panelRootSx } from '../shared/utils/styles.ts';
import { useItemSelection } from '../shared/utils/useItemSelection.ts';
import { MatchedSegmentInterestTags } from './components/MatchedSegmentInterestTags.tsx';
import { MatchedSegmentMetricGroups } from './components/MatchedSegmentMetricGroups.tsx';
import { MatchedSegmentPricing } from './components/MatchedSegmentPricing.tsx';
import { MatchedSegmentTable } from './components/MatchedSegmentTable.tsx';
import { MatchedSegmentToolbar } from './components/MatchedSegmentToolbar.tsx';
import { ALL_DATA_PARTNERS } from './utils/consts.ts';
import type { OpenXAgentMatchedSegment, OpenXAgentMatchedSegmentPanelData } from './utils/types.ts';

type OpenXAgentMatchedSegmentPanelProps = OpenXAgentMatchedSegmentPanelData & {
  onBack?: () => void;
  onEditFilters?: () => void;
  onExport?: (selectedItems: OpenXAgentMatchedSegment[]) => void;
};

export const OpenXAgentMatchedSegmentPanel = ({
  title,
  filters,
  interestTags,
  stats,
  metricGroups,
  pricing,
  items,
  onBack,
  onEditFilters,
  onExport,
}: OpenXAgentMatchedSegmentPanelProps) => {
  const { t } = useTranslation();
  const { closeSidePanel } = useOpenXAgentContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [dataPartner, setDataPartner] = useState<string>(ALL_DATA_PARTNERS);

  const dataPartners = useMemo(() => [...new Set(items.map(({ dataPartner }) => dataPartner))], [items]);

  const visibleItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return items.filter(
      item =>
        (dataPartner === ALL_DATA_PARTNERS || item.dataPartner === dataPartner) &&
        (!query || item.name.toLowerCase().includes(query))
    );
  }, [items, searchQuery, dataPartner]);

  const { selectedIds, selectedItems, toggleItem, selectVisible, selectAllVisible, deselectAllVisible } =
    useItemSelection(items, visibleItems);

  const handleSelectFree = useCallback(() => selectVisible(({ isFree }) => isFree), [selectVisible]);
  const handleExport = useCallback(() => onExport?.(selectedItems), [onExport, selectedItems]);

  return (
    <Box data-test="openx-agent-matched-segment-panel" sx={panelRootSx}>
      <OpenXAgentPanelHeader title={title} onBack={onBack ?? closeSidePanel} onExport={handleExport} />

      <OpenXAgentFilters filters={filters} onEditFilters={onEditFilters} />

      <MatchedSegmentInterestTags tags={interestTags} />

      <OpenXAgentStatTiles stats={stats} maxColumns={3} />

      <MatchedSegmentMetricGroups groups={metricGroups} />

      <MatchedSegmentPricing pricing={pricing} />

      <OpenXAgentPanelSection title={`${t('Matched Segments')} [ ${items.length} ]`}>
        <MatchedSegmentToolbar
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          dataPartner={dataPartner}
          dataPartners={dataPartners}
          onDataPartnerChange={setDataPartner}
        />

        <MatchedSegmentTable
          items={visibleItems}
          selectedIds={selectedIds}
          onToggleItem={toggleItem}
          onSelectAll={selectAllVisible}
          onSelectFree={handleSelectFree}
          onDeselectAll={deselectAllVisible}
        />
      </OpenXAgentPanelSection>
    </Box>
  );
};
